import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useChatStream from '../hooks/useChatStream';
import useFileUpload from '../hooks/useFileUpload';
import MessageList from '../components/chat/MessageList';
import ChatInput from '../components/chat/ChatInput';
import ChatSidebar from '../components/chat/ChatSidebar';
import ChatRestrictionBanner, { ChatUsageIndicator } from '../components/chat/ChatRestrictionBanner';
import { apiRequest } from '../config/api';
import { useAuth } from '../context/AuthContext';

const buildSystemPrompt = (userName, orgName) => {
  let prompt = `You are Able Assistant, a cheerful and knowledgeable mentor for AI agency founders, AI product builders, and anyone exploring the AI space. You help with all kinds of tasks and questions — from business strategy and product design to technical implementation, prompt engineering, and tool selection. You're encouraging, practical, and always happy to help. Keep your responses clear and actionable. Use emojis sparingly — at most one or two per response, not per section. If users ask where to find leads, do prospecting, or run lead generation inside this product, direct them to the Prospecting module in the left sidebar (/prospecting). Mention that they can find companies, narrow by filters like industry and location, and save/enrich lead lists there.`;
  if (userName || orgName) {
    prompt += `\n\nYou are speaking with${userName ? ` ${userName}` : ''}${orgName ? ` from ${orgName}` : ''}. Use their name naturally when appropriate — don't overdo it.`;
  }
  return prompt;
};

export default function ChatApp() {
  const { id: urlChatId } = useParams();
  const navigate = useNavigate();
  const { user, organization } = useAuth();
  const [model, setModel] = useState('sonnet');
  const [thinkingEnabled, setThinkingEnabled] = useState(false);
  const [enabledTools, setEnabledTools] = useState(['web_search', 'code_execution']);
  const [chats, setChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(true);
  const syncedUrlRef = useRef(urlChatId);
  const [usageStatus, setUsageStatus] = useState(null);
  const [restrictionReason, setRestrictionReason] = useState(null);

  // Fetch chat usage status
  const fetchUsageStatus = useCallback(async () => {
    try {
      const res = await apiRequest('/chat/usage-status');
      if (res.ok) {
        const data = await res.json();
        setUsageStatus(data);
        // If on cooldown or over limit, set restriction
        if (data.is_on_cooldown) {
          setRestrictionReason('manual_cooldown');
        } else if (data.usage_percentage >= 100) {
          setRestrictionReason('weekly_limit_exceeded');
        } else {
          setRestrictionReason(null);
        }
      }
    } catch (err) {
      // Silently fail — usage status is non-critical
    }
  }, []);

  // Handle chat_restricted SSE event
  const handleChatRestricted = useCallback((reason, details) => {
    setRestrictionReason(reason);
    // Refresh usage status to get latest numbers
    fetchUsageStatus();
  }, [fetchUsageStatus]);

  // Optimistically add a new chat to the sidebar as soon as the stream starts
  const handleChatCreated = useCallback((newChatId, userText) => {
    const title = userText.length > 60 ? userText.slice(0, 60) + '...' : userText;
    setChats(prev => {
      // Avoid duplicates
      if (prev.some(c => c.id === newChatId)) return prev;
      return [
        { id: newChatId, title, model, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
        ...prev,
      ];
    });
    // Update URL without going through React Router to avoid re-render during streaming.
    // React Router's navigate() uses startTransition, which can process in a separate
    // render cycle from setChatId — causing the URL-sync effect to call loadChat
    // before chatId is updated, wiping the streaming messages.
    window.history.replaceState(null, '', `/chat/${newChatId}`);
    syncedUrlRef.current = newChatId;
  }, [model]);

  const { messages, isStreaming, isLoadingChat, chatId, sendMessage, stopStreaming, clearChat, loadChat } = useChatStream({ onChatCreated: handleChatCreated, onChatRestricted: handleChatRestricted });
  const { files, isUploading, readyFiles, uploadFile, removeFile, clearFiles } = useFileUpload();

  const fetchChats = useCallback(async () => {
    try {
      const res = await apiRequest('/chat/?limit=50');
      if (res.ok) {
        const data = await res.json();
        setChats(data.items);
      }
    } catch (err) {
      console.error('Failed to fetch chats:', err);
    } finally {
      setChatsLoading(false);
    }
  }, []);

  // Load chat list and usage status on mount
  useEffect(() => {
    fetchChats();
    fetchUsageStatus();
  }, [fetchChats, fetchUsageStatus]);

  // Refresh usage status periodically (every 60s)
  useEffect(() => {
    const interval = setInterval(fetchUsageStatus, 60_000);
    return () => clearInterval(interval);
  }, [fetchUsageStatus]);

  // Sync URL → chat state
  useEffect(() => {
    // Skip if we already synced this URL value
    if (syncedUrlRef.current === urlChatId && chatId === (urlChatId || null)) return;
    syncedUrlRef.current = urlChatId;

    if (urlChatId && urlChatId !== chatId && !isStreaming) {
      loadChat(urlChatId);
    } else if (!urlChatId && chatId && !isStreaming) {
      clearChat();
    }
  }, [urlChatId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = async (text) => {
    const systemPrompt = buildSystemPrompt(user?.full_name, organization?.name);
    const filesToSend = readyFiles.length > 0 ? readyFiles : [];
    clearFiles();
    await sendMessage(text, { model, thinkingEnabled, enabledTools, systemPrompt, files: filesToSend });
    // Sync React Router with the actual URL (handleChatCreated used replaceState)
    const pathMatch = window.location.pathname.match(/^\/chat\/(.+)$/);
    if (pathMatch) {
      navigate(window.location.pathname, { replace: true });
    }
    // Refetch to sync real titles and order after stream completes
    fetchChats();
    fetchUsageStatus();
  };

  const handleSelectChat = (id) => {
    if (id === chatId) return;
    navigate(`/chat/${id}`);
  };

  const handleNewChat = () => {
    navigate('/chat');
  };

  const handleRenameChat = async (id, newTitle) => {
    try {
      const res = await apiRequest(`/chat/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ title: newTitle }),
      });
      if (res.ok) {
        setChats(prev => prev.map(c => c.id === id ? { ...c, title: newTitle } : c));
      }
    } catch (err) {
      console.error('Failed to rename chat:', err);
    }
  };

  const handleDeleteChat = async (id) => {
    try {
      const res = await apiRequest(`/chat/${id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        setChats(prev => prev.filter(c => c.id !== id));
        // If deleting the active chat, navigate to clean URL
        if (id === chatId) {
          navigate('/chat');
        }
      }
    } catch (err) {
      console.error('Failed to delete chat:', err);
    }
  };

  return (
    <div className="h-[calc(100vh-var(--app-chrome-height))] flex">
      <ChatSidebar
        chats={chats}
        chatId={chatId}
        onSelectChat={handleSelectChat}
        onNewChat={handleNewChat}
        onRenameChat={handleRenameChat}
        onDeleteChat={handleDeleteChat}
        isLoading={chatsLoading}
      />

      {/* Main chat area */}
      <div className="flex-1 relative min-w-0">
        <MessageList messages={messages} isLoadingChat={isLoadingChat} onSuggestionClick={handleSend} onSendMessage={handleSend} />

        {/* Floating dock overlay */}
        <div className="absolute bottom-0 inset-x-0 pointer-events-none">
          {/* Gradient fade */}
          <div className="h-16 bg-gradient-to-t from-white dark:from-gray-900 to-transparent" />

          {/* Input dock */}
          <div className="bg-white dark:bg-gray-900 pointer-events-auto pb-4 px-4">
            {restrictionReason && (
              <ChatRestrictionBanner reason={restrictionReason} usageStatus={usageStatus} />
            )}
            <ChatInput
              onSend={handleSend}
              onStop={stopStreaming}
              isStreaming={isStreaming}
              disabled={!!restrictionReason}
              model={model}
              setModel={setModel}
              thinkingEnabled={thinkingEnabled}
              setThinkingEnabled={setThinkingEnabled}
              enabledTools={enabledTools}
              setEnabledTools={setEnabledTools}
              onClearChat={handleNewChat}
              hasMessages={messages.length > 0}
              files={files}
              isUploading={isUploading}
              onUploadFile={uploadFile}
              onRemoveFile={removeFile}
            />
            {!restrictionReason && <ChatUsageIndicator usageStatus={usageStatus} />}
          </div>
        </div>
      </div>
    </div>
  );
}
